class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    }
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer){
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop){
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl) ?
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event) :
        FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer').innerHTML;
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements =
      parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter');
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    }
    const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      // BJP-291: Dawn ships this line without a null guard, which throws
      // "Cannot set properties of null (setting 'innerHTML')" whenever the new
      // markup contains a filter that isn't in the current DOM.
      //
      // That happens on this store every time a filter is applied from an
      // unfiltered state: Shopify only adds the Price filter to
      // `results.filters` once something is active, so the fresh HTML carries an
      // accordion the page has never rendered. renderFilters only ever REPLACES
      // existing nodes, so there is nothing to write into.
      //
      // The throw aborted the rest of the method, meaning renderActiveFacets /
      // renderAdditionalElements / renderCounts below never ran — the applied
      // filter chips and counts silently stopped updating. Skipping the unknown
      // filter keeps the remaining updates working. The new filter appears on
      // the next full page load.
      const target = document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`);
      if (!target) return;
      target.innerHTML = element.innerHTML;
    });

    // BIL-143 / #814: renderFilters only ever REPLACES an accordion, it never
    // removes one. The two Size accordions are the only filters on this store
    // whose PRESENCE depends on the other filters — Shoe Size renders only where
    // shoes survive the current filter set — so clearing a shoe size on a page
    // whose remaining filters hold no shoes left the old Shoe Size accordion
    // sitting there: still ticked, with its hidden product-type scope still
    // enabled, ready to re-apply itself on the shopper's next interaction.
    //
    // Neutralise and hide any size accordion the new result set didn't render,
    // and un-hide one that has come back. Guarded on the response actually
    // carrying the drawer, so a section render without it changes nothing.
    const liveSizeAccordions = document.querySelectorAll('[data-pd-size-kind]');
    if (liveSizeAccordions.length && parsedHTML.querySelector('#FacetFiltersFormMobile')) {
      const freshSizeKinds = new Set(
        Array.from(parsedHTML.querySelectorAll('[data-pd-size-kind]')).map((element) =>
          element.getAttribute('data-pd-size-kind')
        )
      );
      liveSizeAccordions.forEach((element) => {
        const stale = !freshSizeKinds.has(element.getAttribute('data-pd-size-kind'));
        if (stale) {
          element.querySelectorAll('input').forEach((input) => {
            input.checked = false;
            input.disabled = true;
          });
        }
        // Explicit marker rather than relying on `hidden`: pd-facets-drawer.js
        // also owns `hidden` on these accordions (the scope probe collapses one
        // with nothing left to show), and it must not paint a stale accordion
        // back onto the screen from its cache.
        element.toggleAttribute('data-pd-stale', stale);
        element.hidden = stale;
      });
    }

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop', '.pd-facets-drawer__applied'];

    activeFacetElementSelectors.forEach((selector) => {
      const sourceEl = html.querySelector(selector);
      const targetEl = document.querySelector(selector);
      if (!targetEl) return;
      // Source may be missing when there are zero active filters (the
      // drawer applied-chips wrapper renders empty in that state); fall
      // back to clearing the target so stale chips disappear.
      targetEl.innerHTML = sourceEl ? sourceEl.innerHTML : '';
    })

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile')?.closest('menu-drawer')?.bindEvents?.();
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector('.facets__summary').outerHTML = source.querySelector('.facets__summary').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      }
    ]
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'))
      this.onSubmitForm(searchParams, event)
    } else {
      const forms = [];
      const eventForm = event.target.closest('form');
      const isMobile = eventForm.id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          } else if (form.id === 'FacetFiltersFormMobile') {
            // In drawer mode the active filter inputs live in the drawer form
            // on every viewport, so a banner sort submit must carry them too.
            // Only the `filter.*` keys — sort_by belongs to the event's form,
            // and q / options[prefix] already come from the sort form on search.
            const filterParams = new URLSearchParams();
            new URLSearchParams(new FormData(form)).forEach((value, key) => {
              if (key.startsWith('filter.')) filterParams.append(key, value);
            });
            const filterString = filterParams.toString();
            if (filterString) forms.push(filterString);
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      FacetFiltersForm.syncSortInputs(eventForm);
      this.onSubmitForm(forms.join('&'), event)
    }
  }

  // sort_by is rendered twice (banner <select> + the drawer's mobile sort
  // radios). Mirror the submitting form's value into the other forms so a
  // later submit from either one doesn't resurrect a stale sort.
  static syncSortInputs(sourceForm) {
    const source = sourceForm.querySelector('select[name="sort_by"], input[name="sort_by"]:checked');
    if (!source) return;
    document.querySelectorAll('facet-filters-form form').forEach((form) => {
      if (form === sourceForm) return;
      form.querySelectorAll('select[name="sort_by"]').forEach((select) => {
        select.value = source.value;
      });
      form.querySelectorAll('input[name="sort_by"]').forEach((radio) => {
        radio.checked = radio.value === source.value;
      });
    });
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input')
      .forEach(element => element.addEventListener('change', this.onRangeChange.bind(this)));
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
